/**
 * Evidence Lineage Page - Enhanced Production Version
 * Trace from Claim ← Evidence ← Source with full lineage visualization
 * Includes Flow Diagram and Table View modes
 * Implements Superpower #1: Cross-Framework Intelligence
 */
import React, { useState, useEffect } from 'react';
import { 
  Link2, 
  FileText, 
  Database,
  CheckCircle2,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  Search,
  Eye,
  ChevronDown,
  ChevronRight as ChevronRightIcon,
  Loader2,
  RefreshCw,
  AlertTriangle,
  Hash,
  Clock,
  User,
  GitBranch,
  Layers,
  Target,
  Zap,
  ExternalLink,
  LayoutGrid,
  Table,
  Shield
} from 'lucide-react';
import { apiFetch } from '../../services/api_service';

// Flow Diagram Component - Visual representation of lineage
function LineageFlowDiagram({ lineage }) {
  const { lineage_graph, file_name, uploaded_at, quality_metrics } = lineage;

  const navigateTo = (path) => {
    window.location.href = path;
  };

  return (
    <div className="bg-[#111111] rounded-xl border border-[#1F1F1F] p-6">
      <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
        <LayoutGrid className="h-5 w-5 text-teal-400" />
        Data Flow Diagram
      </h3>

      <div className="space-y-6">
        {/* UPSTREAM: Source */}
        <div>
          <div className="text-xs font-semibold text-[#6B6B6B] uppercase mb-3 flex items-center gap-2">
            <ArrowUp size={14} className="text-blue-400" />
            Upstream (Source)
          </div>

          {lineage_graph?.upstream?.obligation ? (
            <div 
              className="p-4 rounded-lg bg-blue-500/10 border-2 border-blue-500/30 hover:border-blue-500/50 transition-colors cursor-pointer"
              onClick={() => navigateTo(`/obligations/action-queue?selected=${lineage_graph.upstream.obligation.obligation_id}`)}
            >
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0">
                  <Zap className="h-6 w-6 text-blue-400" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-white">
                    Obligation: {lineage_graph.upstream.obligation.obligation_code}
                  </p>
                  <p className="text-xs text-[#A0A0A0]">
                    {lineage_graph.upstream.obligation.obligation_title}
                  </p>
                  <p className="text-xs text-[#6B6B6B] mt-1">
                    Owner: {lineage_graph.upstream.obligation.obligation_owner || 'N/A'}
                  </p>
                </div>
                <ArrowRight size={16} className="text-blue-400" />
              </div>
            </div>
          ) : lineage_graph?.upstream?.creator ? (
            <div className="p-4 rounded-lg bg-blue-500/10 border-2 border-blue-500/30">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0">
                  <User className="h-6 w-6 text-blue-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">
                    {lineage_graph.upstream.creator.user_name || 'Unknown User'}
                  </p>
                  <p className="text-xs text-[#A0A0A0]">
                    {lineage_graph.upstream.creator.user_role}
                  </p>
                  <p className="text-xs text-[#6B6B6B] mt-1">
                    {lineage_graph.upstream.creator.department}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 rounded-lg bg-[#0A0A0A] border-2 border-dashed border-[#2F2F2F]">
              <p className="text-sm text-[#6B6B6B] text-center">
                Direct upload (no obligation linked)
              </p>
            </div>
          )}
        </div>

        {/* Arrow Down */}
        <div className="flex justify-center">
          <div className="flex flex-col items-center">
            <div className="h-6 w-0.5 bg-gradient-to-b from-blue-500/50 to-teal-500/50"></div>
            <ArrowDown className="h-5 w-5 text-teal-400" />
            <div className="h-6 w-0.5 bg-gradient-to-b from-teal-500/50 to-purple-500/50"></div>
          </div>
        </div>

        {/* CURRENT: Evidence */}
        <div>
          <div className="text-xs font-semibold text-[#6B6B6B] uppercase mb-3 flex items-center gap-2">
            <FileText size={14} className="text-teal-400" />
            Current Evidence
          </div>

          <div className="p-4 rounded-lg bg-gradient-to-br from-teal-500/10 to-emerald-500/10 border-4 border-teal-500/50 shadow-lg">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-teal-500/20 flex items-center justify-center shrink-0">
                <FileText className="h-6 w-6 text-teal-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-white">
                  {file_name || 'Unnamed Evidence'}
                </p>
                <p className="text-xs text-[#A0A0A0]">
                  Evidence file (you are here)
                </p>
                <p className="text-xs text-[#6B6B6B] mt-1">
                  Uploaded: {uploaded_at ? new Date(uploaded_at).toLocaleDateString() : 'Unknown'}
                </p>
              </div>
              {quality_metrics?.approval_status === 'approved' && (
                <span className="px-2 py-1 rounded bg-emerald-500/20 text-emerald-400 text-xs font-medium">
                  ✓ Approved
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Arrow Down */}
        <div className="flex justify-center">
          <div className="flex flex-col items-center">
            <div className="h-6 w-0.5 bg-gradient-to-b from-teal-500/50 to-purple-500/50"></div>
            <ArrowDown className="h-5 w-5 text-purple-400" />
            <div className="h-6 w-0.5 bg-gradient-to-b from-purple-500/50 to-purple-500/30"></div>
          </div>
        </div>

        {/* DOWNSTREAM: Claims */}
        <div>
          <div className="text-xs font-semibold text-[#6B6B6B] uppercase mb-3 flex items-center justify-between">
            <span className="flex items-center gap-2">
              <ArrowDown size={14} className="text-purple-400" />
              Downstream (Usage)
            </span>
            <span className="px-2 py-1 rounded bg-purple-500/20 text-purple-400 text-xs font-medium normal-case">
              Superpower #1
            </span>
          </div>

          {/* Claims */}
          <div className="space-y-3">
            <p className="text-sm font-semibold text-white flex items-center gap-2">
              <Database size={14} className="text-purple-400" />
              Claims ({lineage_graph?.downstream?.claims?.length || 0})
            </p>

            {!lineage_graph?.downstream?.claims?.length ? (
              <div className="p-4 rounded-lg bg-[#0A0A0A] border-2 border-dashed border-[#2F2F2F]">
                <p className="text-sm text-[#6B6B6B] text-center">
                  Not yet used in any disclosure claims
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {lineage_graph.downstream.claims.map((claim, idx) => (
                  <div
                    key={idx}
                    className="p-3 rounded-lg bg-purple-500/10 border-2 border-purple-500/30 hover:border-purple-500/50 transition-colors cursor-pointer"
                    onClick={() => navigateTo(`/disclosures?claim=${claim.claim_id}`)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="px-2 py-0.5 rounded bg-[#1F1F1F] text-white text-xs font-medium">
                            {claim.framework_code}
                          </span>
                          <span className="px-2 py-0.5 rounded bg-[#1F1F1F] text-[#A0A0A0] text-xs font-mono">
                            {claim.requirement_code}
                          </span>
                          {claim.claim_status === 'approved' && (
                            <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-xs">
                              ✓ Approved
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-[#A0A0A0] mb-1">
                          {claim.requirement_title}
                        </p>
                        <p className="text-xs font-semibold text-white">
                          Value: {claim.claim_value || 'Not set'}
                        </p>
                      </div>
                      <ArrowRight size={14} className="text-purple-400 shrink-0" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Disclosure Cycles */}
          {lineage_graph?.downstream?.disclosure_cycles?.length > 0 && (
            <div className="mt-4 space-y-3">
              <p className="text-sm font-semibold text-white flex items-center gap-2">
                <Layers size={14} className="text-indigo-400" />
                Disclosure Cycles ({lineage_graph.downstream.disclosure_cycles.length})
              </p>
              
              <div className="space-y-2">
                {lineage_graph.downstream.disclosure_cycles.map((cycle, idx) => (
                  <div
                    key={idx}
                    className="p-3 rounded-lg bg-indigo-500/10 border-2 border-indigo-500/30 hover:border-indigo-500/50 transition-colors cursor-pointer"
                    onClick={() => navigateTo(`/disclosures/${cycle.cycle_id}`)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-white">
                          {cycle.cycle_name}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="px-2 py-0.5 rounded bg-[#1F1F1F] text-[#A0A0A0] text-xs">
                            {cycle.cycle_type}
                          </span>
                          <span className="px-2 py-0.5 rounded bg-[#1F1F1F] text-[#A0A0A0] text-xs">
                            {cycle.cycle_status}
                          </span>
                          <span className="text-xs text-[#6B6B6B]">
                            {cycle.claims_in_cycle} claims
                          </span>
                        </div>
                      </div>
                      <ArrowRight size={14} className="text-indigo-400" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Table View Component - Tabular representation of lineage
function LineageTableView({ lineage }) {
  const { lineage_graph } = lineage;

  return (
    <div className="bg-[#111111] rounded-xl border border-[#1F1F1F] p-6">
      <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
        <Table className="h-5 w-5 text-teal-400" />
        Relationship Table View
      </h3>

      <div className="space-y-6">
        {/* Upstream Table */}
        <div>
          <h4 className="text-sm font-semibold text-white mb-3">Upstream (Source)</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#1F1F1F]">
                  <th className="text-left py-2 px-3 text-xs font-semibold text-[#6B6B6B]">Type</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-[#6B6B6B]">ID</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-[#6B6B6B]">Description</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-[#6B6B6B]">Owner</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-[#6B6B6B]">Action</th>
                </tr>
              </thead>
              <tbody>
                {lineage_graph?.upstream?.obligation ? (
                  <tr className="border-b border-[#1F1F1F] hover:bg-[#0A0A0A]">
                    <td className="py-2 px-3 text-white">
                      <span className="flex items-center gap-2">
                        <Zap size={14} className="text-blue-400" />
                        Obligation
                      </span>
                    </td>
                    <td className="py-2 px-3 font-mono text-xs text-[#A0A0A0]">
                      {lineage_graph.upstream.obligation.obligation_code}
                    </td>
                    <td className="py-2 px-3 text-[#A0A0A0]">
                      {lineage_graph.upstream.obligation.obligation_title}
                    </td>
                    <td className="py-2 px-3 text-[#A0A0A0]">
                      {lineage_graph.upstream.obligation.obligation_owner || 'N/A'}
                    </td>
                    <td className="py-2 px-3 text-right">
                      <button className="text-teal-400 hover:text-teal-300 text-xs">
                        View →
                      </button>
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td colSpan="5" className="py-4 px-3 text-center text-[#6B6B6B] text-xs">
                      No upstream sources
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Downstream Table */}
        <div>
          <h4 className="text-sm font-semibold text-white mb-3">
            Downstream (Usage) - {lineage_graph?.downstream?.claims?.length || 0} Claims
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#1F1F1F]">
                  <th className="text-left py-2 px-3 text-xs font-semibold text-[#6B6B6B]">Framework</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-[#6B6B6B]">Requirement</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-[#6B6B6B]">Data Point</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-[#6B6B6B]">Value</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-[#6B6B6B]">Status</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-[#6B6B6B]">Action</th>
                </tr>
              </thead>
              <tbody>
                {!lineage_graph?.downstream?.claims?.length ? (
                  <tr>
                    <td colSpan="6" className="py-4 px-3 text-center text-[#6B6B6B] text-xs">
                      Not yet used in any claims
                    </td>
                  </tr>
                ) : (
                  lineage_graph.downstream.claims.map((claim, idx) => (
                    <tr key={idx} className="border-b border-[#1F1F1F] hover:bg-[#0A0A0A]">
                      <td className="py-2 px-3">
                        <span className="px-2 py-0.5 rounded bg-[#1F1F1F] text-white text-xs">
                          {claim.framework_code}
                        </span>
                      </td>
                      <td className="py-2 px-3 font-mono text-xs text-[#A0A0A0]">
                        {claim.requirement_code}
                      </td>
                      <td className="py-2 px-3 text-xs text-[#A0A0A0]">
                        {claim.data_point_identifier || claim.data_point_title || '-'}
                      </td>
                      <td className="py-2 px-3 font-semibold text-white">
                        {claim.claim_value || '-'}
                      </td>
                      <td className="py-2 px-3">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          claim.claim_status === 'approved'
                            ? 'bg-emerald-500/20 text-emerald-400'
                            : 'bg-amber-500/20 text-amber-400'
                        }`}>
                          {claim.claim_status}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-right">
                        <button className="text-teal-400 hover:text-teal-300 text-xs">
                          View →
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Transformations Table */}
        {lineage_graph?.transformations?.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-white mb-3">
              Transformations ({lineage_graph.transformations.length})
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#1F1F1F]">
                    <th className="text-left py-2 px-3 text-xs font-semibold text-[#6B6B6B]">Type</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-[#6B6B6B]">Description</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-[#6B6B6B]">Performed By</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-[#6B6B6B]">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {lineage_graph.transformations.map((transform, idx) => (
                    <tr key={idx} className="border-b border-[#1F1F1F] hover:bg-[#0A0A0A]">
                      <td className="py-2 px-3">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          transform.transformation_type === 'upload' ? 'bg-emerald-500/20 text-emerald-400' :
                          transform.transformation_type === 'approval' ? 'bg-blue-500/20 text-blue-400' :
                          transform.transformation_type === 'data_validation' ? 'bg-teal-500/20 text-teal-400' :
                          'bg-[#1F1F1F] text-[#A0A0A0]'
                        }`}>
                          {transform.transformation_type?.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-[#A0A0A0]">{transform.description}</td>
                      <td className="py-2 px-3 text-[#A0A0A0]">{transform.performed_by_name || 'System'}</td>
                      <td className="py-2 px-3 text-xs text-[#6B6B6B]">
                        {transform.performed_at ? new Date(transform.performed_at).toLocaleString() : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Impact Analysis Card - Cross-framework intelligence visualization
function ImpactAnalysisCard({ impact }) {
  if (!impact) return null;

  return (
    <div className="bg-gradient-to-br from-teal-500/10 to-emerald-500/10 rounded-xl border border-teal-500/30 p-6">
      <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <Zap className="h-5 w-5 text-teal-400" />
        Cross-Framework Impact
        <span className="px-2 py-1 rounded bg-teal-500/20 text-teal-400 text-xs font-medium">
          Superpower #1
        </span>
      </h4>

      <div className="grid md:grid-cols-4 gap-4 mb-4">
        <div className="text-center p-3 rounded-lg bg-[#0A0A0A] border border-[#1F1F1F]">
          <div className="text-2xl font-bold text-white">{impact.framework_count || 0}</div>
          <div className="text-xs text-[#6B6B6B]">Frameworks</div>
        </div>
        <div className="text-center p-3 rounded-lg bg-[#0A0A0A] border border-[#1F1F1F]">
          <div className="text-2xl font-bold text-white">{impact.requirements_satisfied || 0}</div>
          <div className="text-xs text-[#6B6B6B]">Requirements</div>
        </div>
        <div className="text-center p-3 rounded-lg bg-[#0A0A0A] border border-[#1F1F1F]">
          <div className="text-2xl font-bold text-emerald-400">{impact.claims_approved || 0}</div>
          <div className="text-xs text-[#6B6B6B]">Approved Claims</div>
        </div>
        <div className="text-center p-3 rounded-lg bg-[#0A0A0A] border border-[#1F1F1F]">
          <div className="text-2xl font-bold text-teal-400">{impact.upload_efficiency || 1}x</div>
          <div className="text-xs text-[#6B6B6B]">Reuse Efficiency</div>
        </div>
      </div>

      {impact.frameworks_impacted?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {impact.frameworks_impacted.map((fw, idx) => (
            <span key={idx} className="px-3 py-1 rounded-full bg-teal-500/20 text-teal-400 text-xs font-medium">
              {fw}
            </span>
          ))}
        </div>
      )}

      {impact.efficiency_message && (
        <p className="text-sm text-teal-300 italic">
          {impact.efficiency_message}
        </p>
      )}
    </div>
  );
}

// Quality Metrics Card
function QualityMetricsCard({ metrics }) {
  if (!metrics) return null;

  return (
    <div className="bg-[#111111] rounded-xl border border-[#1F1F1F] p-6">
      <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <CheckCircle2 className="h-5 w-5 text-emerald-400" />
        Quality Metrics
      </h4>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <p className="text-sm text-[#6B6B6B] mb-1">Approval Status</p>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded text-sm font-medium ${
              metrics.approval_status === 'approved'
                ? 'bg-emerald-500/20 text-emerald-400'
                : 'bg-amber-500/20 text-amber-400'
            }`}>
              {metrics.approval_status}
            </span>
            {metrics.approved_by && (
              <span className="text-sm text-[#6B6B6B]">
                by {metrics.approved_by}
              </span>
            )}
          </div>
        </div>

        <div>
          <p className="text-sm text-[#6B6B6B] mb-1">Verification Level</p>
          <span className="px-3 py-1 rounded bg-blue-500/20 text-blue-400 text-sm font-medium">
            {metrics.verification_level?.replace(/_/g, ' ')}
          </span>
        </div>

        <div>
          <p className="text-sm text-[#6B6B6B] mb-1">Data Quality Score</p>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-[#1F1F1F] rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-500"
                style={{ width: `${metrics.data_quality_score || 0}%` }}
              ></div>
            </div>
            <span className="text-sm font-semibold text-white">
              {metrics.data_quality_score || 0}%
            </span>
          </div>
        </div>

        <div>
          <p className="text-sm text-[#6B6B6B] mb-1">Audit Trail Completeness</p>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-[#1F1F1F] rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500"
                style={{ width: `${metrics.audit_trail_completeness || 0}%` }}
              ></div>
            </div>
            <span className="text-sm font-semibold text-white">
              {metrics.audit_trail_completeness || 0}%
            </span>
          </div>
          <p className="text-xs text-[#6B6B6B] mt-1">
            {metrics.audit_event_count || 0} events logged
          </p>
        </div>
      </div>
    </div>
  );
}

// Evidence Detail Panel - Full lineage view for selected evidence
function EvidenceDetailPanel({ lineage, onRefresh, onClose }) {
  const [viewMode, setViewMode] = useState('flow'); // 'flow' or 'table'

  if (!lineage) return null;

  const { impact_analysis, quality_metrics, file_name, evidence_id, uploaded_at, uploaded_by_name } = lineage;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-teal-500/10 to-emerald-500/10 rounded-xl border border-teal-500/30 p-6">
        <div className="flex items-start gap-4">
          <div className="h-16 w-16 rounded-full bg-gradient-to-br from-teal-500/20 to-emerald-500/20 flex items-center justify-center shrink-0">
            <FileText className="h-8 w-8 text-teal-400" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-white mb-2">
              {file_name || 'Unnamed Evidence'}
            </h2>
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className="px-2 py-1 rounded bg-[#1F1F1F] text-[#A0A0A0] text-xs font-mono">
                {evidence_id?.substring(0, 12)}...
              </span>
              <span className="px-2 py-1 rounded bg-emerald-500/20 text-emerald-400 text-xs">
                {quality_metrics?.verification_level?.replace(/_/g, ' ')}
              </span>
              {quality_metrics?.approval_status === 'approved' && (
                <span className="px-2 py-1 rounded bg-blue-500/20 text-blue-400 text-xs">
                  ✓ Approved
                </span>
              )}
            </div>

            <div className="flex items-center gap-4 text-sm flex-wrap text-[#A0A0A0]">
              <span>Frameworks: <strong className="text-white">{impact_analysis?.framework_count || 0}</strong></span>
              <span>Requirements: <strong className="text-white">{impact_analysis?.requirements_satisfied || 0}</strong></span>
              <span>Claims: <strong className="text-white">{impact_analysis?.claims_supported || 0}</strong></span>
              <span>Efficiency: <strong className="text-teal-400">{impact_analysis?.upload_efficiency || 1}x</strong></span>
            </div>
          </div>
          <button
            onClick={onRefresh}
            className="p-2 rounded-lg hover:bg-[#1F1F1F] text-[#6B6B6B] hover:text-white transition-colors"
            title="Refresh Lineage"
          >
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {/* View Mode Toggle */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setViewMode('flow')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            viewMode === 'flow'
              ? 'bg-teal-500 text-white'
              : 'bg-[#111111] border border-[#1F1F1F] text-[#A0A0A0] hover:bg-[#1F1F1F]'
          }`}
        >
          <LayoutGrid size={16} />
          Flow Diagram
        </button>
        <button
          onClick={() => setViewMode('table')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            viewMode === 'table'
              ? 'bg-teal-500 text-white'
              : 'bg-[#111111] border border-[#1F1F1F] text-[#A0A0A0] hover:bg-[#1F1F1F]'
          }`}
        >
          <Table size={16} />
          Table View
        </button>
      </div>

      {/* Lineage Visualization */}
      {viewMode === 'flow' ? (
        <LineageFlowDiagram lineage={lineage} />
      ) : (
        <LineageTableView lineage={lineage} />
      )}

      {/* Impact Analysis */}
      <ImpactAnalysisCard impact={impact_analysis} />

      {/* Quality Metrics */}
      <QualityMetricsCard metrics={quality_metrics} />

      {/* Audit Trail Link */}
      <div className="bg-[#0A0A14] rounded-xl p-6 border border-[#1F1F1F]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="h-8 w-8 text-purple-400" />
            <div>
              <p className="text-base font-semibold text-white mb-1 flex items-center gap-2">
                Complete Audit Trail Available
                <span className="px-2 py-1 rounded bg-purple-500/20 text-purple-400 text-xs font-medium">
                  Superpower #3
                </span>
              </p>
              <p className="text-sm text-[#A0A0A0]">
                View every action taken on this evidence with cryptographic verification
              </p>
            </div>
          </div>
          <button
            onClick={() => window.location.href = `/audit/trail?aggregateType=Evidence&aggregateId=${evidence_id}`}
            className="px-6 py-3 rounded-lg bg-purple-500 hover:bg-purple-600 text-white font-semibold transition-colors flex items-center gap-2"
          >
            View Audit Trail
            <ExternalLink size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

// Evidence List Item
function EvidenceListItem({ evidence, isSelected, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-3 rounded-lg transition-all ${
        isSelected
          ? 'bg-teal-500/10 border-2 border-teal-500'
          : 'hover:bg-[#1F1F1F] border-2 border-transparent'
      }`}
      data-testid={`evidence-item-${evidence.evidence_id}`}
    >
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-full bg-teal-500/20 flex items-center justify-center shrink-0">
          <FileText className="h-5 w-5 text-teal-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-white truncate">
            {evidence.file_name || 'Unnamed Evidence'}
          </p>
          <p className="text-xs text-[#6B6B6B]">
            {evidence.uploaded_at ? new Date(evidence.uploaded_at).toLocaleDateString() : 'Unknown date'}
          </p>
          {evidence.impact_analysis?.framework_count > 0 && (
            <p className="text-xs text-teal-400 mt-1">
              {evidence.impact_analysis.framework_count} frameworks • {evidence.impact_analysis.upload_efficiency}x reuse
            </p>
          )}
        </div>
      </div>
    </button>
  );
}

// Main Export - Evidence Lineage Page
export default function EvidenceLineagePage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lineages, setLineages] = useState([]);
  const [selectedEvidence, setSelectedEvidence] = useState(null);
  const [selectedLineage, setSelectedLineage] = useState(null);
  const [lineageLoading, setLineageLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCycle, setSelectedCycle] = useState('');
  const [cycles, setCycles] = useState([]);

  // Fetch cycles
  const fetchCycles = async () => {
    try {
      const data = await apiFetch('/disclosure-cycles/list?limit=20');
      if (!data?._error) {
        setCycles(data.items || []);
      }
    } catch (err) {
      console.error('Error fetching cycles:', err);
    }
  };

  // Fetch lineages (list view)
  const fetchLineages = async () => {
    setLoading(true);
    setError(null);
    try {
      const path = selectedCycle
        ? `/assurance/evidence-lineage?cycle_id=${selectedCycle}&limit=50`
        : `/assurance/evidence-lineage?limit=50`;
      
      const data = await apiFetch(path);
      if (data?._error) throw new Error(data.message || 'Failed to fetch lineage data');
      setLineages(data.lineages || []);
    } catch (err) {
      console.error('Error fetching lineages:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch detailed lineage for selected evidence
  const fetchDetailedLineage = async (evidenceId) => {
    setSelectedEvidence(evidenceId);
    setLineageLoading(true);
    try {
      const data = await apiFetch(`/assurance/evidence-lineage/${evidenceId}`);
      if (!data?._error) {
        setSelectedLineage(data);
      }
    } catch (err) {
      console.error('Error fetching detailed lineage:', err);
    } finally {
      setLineageLoading(false);
    }
  };

  // Refresh selected lineage
  const handleRefreshLineage = async () => {
    if (!selectedEvidence) return;
    setLineageLoading(true);
    try {
      const updated = await apiFetch(`/assurance/evidence-lineage/${selectedEvidence}/refresh`, {
        method: 'POST'
      });
      if (!updated?._error) {
        setSelectedLineage(updated);
        setLineages(prev => prev.map(l => l.evidence_id === selectedEvidence ? updated : l));
      }
    } catch (err) {
      console.error('Error refreshing lineage:', err);
    } finally {
      setLineageLoading(false);
    }
  };

  useEffect(() => {
    fetchCycles();
    fetchLineages();
  }, []);

  useEffect(() => {
    fetchLineages();
    setSelectedEvidence(null);
    setSelectedLineage(null);
  }, [selectedCycle]);

  // Filter lineages
  const filteredLineages = lineages.filter(lineage =>
    (lineage.file_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (lineage.impact_analysis?.frameworks_impacted || []).some(fw => 
      fw.toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  // Calculate stats
  const totalEvidence = lineages.length;
  const totalMappings = lineages.reduce((sum, l) => sum + (l.impact_analysis?.requirements_satisfied || 0), 0);
  const totalFrameworks = new Set(lineages.flatMap(l => l.impact_analysis?.frameworks_impacted || [])).size;
  const avgReuse = lineages.length > 0 
    ? (lineages.reduce((sum, l) => sum + (l.impact_analysis?.upload_efficiency || 1), 0) / lineages.length).toFixed(1)
    : 0;

  if (loading && lineages.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-teal-400" />
      </div>
    );
  }

  if (error && lineages.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <AlertTriangle className="h-12 w-12 text-red-400 mb-4" />
        <h3 className="text-lg font-medium text-white mb-2">Error Loading Lineage Data</h3>
        <p className="text-sm text-[#6B6B6B] mb-4">{error}</p>
        <button
          onClick={fetchLineages}
          className="flex items-center gap-2 px-4 py-2 bg-teal-500/20 hover:bg-teal-500/30 text-teal-400 rounded-lg"
        >
          <RefreshCw size={16} />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="evidence-lineage-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-teal-700 via-teal-600 to-cyan-700 rounded-xl p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-sm text-white/80 mb-2 flex items-center gap-2">
              <Shield size={14} />
              Audit & Assurance
              <span className="mx-1">›</span>
              Evidence Lineage
            </p>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Link2 className="h-7 w-7" />
              Evidence Lineage
              <span className="px-3 py-1 rounded-full bg-white/20 text-white text-sm font-medium">
                Superpower #1
              </span>
            </h1>
            <p className="text-white/90 mt-2">
              Trace evidence from upload to cross-framework requirements
            </p>
          </div>
          <button 
            onClick={fetchLineages}
            disabled={loading}
            className="p-2 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Quick Stats */}
        <div className="grid md:grid-cols-4 gap-4">
          <div className="p-4 rounded-lg bg-white/10 border border-white/20">
            <p className="text-xs text-white/80 mb-1">Evidence Items</p>
            <p className="text-3xl font-bold text-white">{totalEvidence}</p>
          </div>
          <div className="p-4 rounded-lg bg-white/10 border border-white/20">
            <p className="text-xs text-white/80 mb-1">Requirements Satisfied</p>
            <p className="text-3xl font-bold text-white">{totalMappings}</p>
          </div>
          <div className="p-4 rounded-lg bg-white/10 border border-white/20">
            <p className="text-xs text-white/80 mb-1">Frameworks Covered</p>
            <p className="text-3xl font-bold text-white">{totalFrameworks}</p>
          </div>
          <div className="p-4 rounded-lg bg-emerald-500/20 border border-emerald-400/30">
            <p className="text-xs text-white/80 mb-1">Avg Reuse Efficiency</p>
            <p className="text-3xl font-bold text-emerald-300">{avgReuse}x</p>
            <p className="text-xs text-emerald-200 mt-1">Cross-framework intelligence</p>
          </div>
        </div>
      </div>

      {/* Main Content - Split Panel */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Panel - Evidence List */}
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl overflow-hidden">
          <div className="p-4 border-b border-[#1F1F1F] sticky top-0 bg-[#111111] z-10">
            <h3 className="text-base font-semibold text-white mb-3">Select Evidence</h3>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" />
              <input
                type="text"
                placeholder="Search evidence..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg text-sm text-white placeholder:text-[#6B6B6B] focus:border-teal-500/50 focus:outline-none"
              />
            </div>
            {cycles.length > 0 && (
              <select
                value={selectedCycle}
                onChange={(e) => setSelectedCycle(e.target.value)}
                className="w-full mt-2 px-3 py-2 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg text-sm text-white focus:outline-none focus:border-teal-500/50"
              >
                <option value="">All Cycles</option>
                {cycles.map((cycle) => (
                  <option key={cycle.id} value={cycle.id}>
                    {cycle.cycle_name}
                  </option>
                ))}
              </select>
            )}
            <p className="text-xs text-[#6B6B6B] mt-2">
              {filteredLineages.length} of {lineages.length} files
            </p>
          </div>

          <div className="p-2 overflow-y-auto" style={{ maxHeight: '600px' }}>
            {filteredLineages.length === 0 ? (
              <div className="p-8 text-center">
                <Link2 size={32} className="mx-auto text-[#2F2F2F] mb-3" />
                <p className="text-sm text-[#6B6B6B]">No evidence found</p>
              </div>
            ) : (
              <div className="space-y-1">
                {filteredLineages.map((lineage) => (
                  <EvidenceListItem
                    key={lineage.evidence_id}
                    evidence={lineage}
                    isSelected={selectedEvidence === lineage.evidence_id}
                    onClick={() => fetchDetailedLineage(lineage.evidence_id)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Lineage Detail */}
        <div className="lg:col-span-2">
          {!selectedLineage && !lineageLoading ? (
            <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-12 text-center h-full flex flex-col items-center justify-center">
              <Link2 size={64} className="text-[#2F2F2F] mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">
                Select Evidence to View Lineage
              </h3>
              <p className="text-[#6B6B6B] max-w-md">
                Choose an evidence file from the list to visualize its complete
                lineage from source to disclosure.
              </p>
            </div>
          ) : lineageLoading ? (
            <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-12 flex items-center justify-center h-full">
              <div className="text-center">
                <Loader2 className="h-12 w-12 animate-spin text-teal-400 mx-auto mb-4" />
                <p className="text-sm text-[#6B6B6B]">Building lineage graph...</p>
              </div>
            </div>
          ) : (
            <EvidenceDetailPanel
              lineage={selectedLineage}
              onRefresh={handleRefreshLineage}
              onClose={() => {
                setSelectedEvidence(null);
                setSelectedLineage(null);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
